import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import { type UiLanguageCode, useUiLanguage } from '@eluve/i18n';
import { useNamedLogger } from '@eluve/logger';

import {
  tenantUserSettingFragment,
  updateUiLanguageMutation,
} from './operations';

interface UpdateUiLanguageCallbacks {
  onSuccess?: (code: UiLanguageCode) => void;
  onError?: () => void;
  onSkip?: () => void;
}

/**
 * Returns a setter that updates the active UI language. The local I18n
 * context is updated immediately (instant UI flip + localStorage write
 * through `useUiLanguage`'s setter), and a mutation persists the value to
 * `tenant_user_settings.ui_language`. The mutation is skipped when the
 * value already matches the user's explicit override, so re-picking an
 * existing override is a true no-op.
 *
 * Both `tenantId` and `userId` are required. Callers that don't yet have
 * a session (e.g. the floating window header pre-auth) must not call this
 * hook — gate the rendering of the consuming subtree on auth instead.
 *
 * `onSuccess` fires only after the value is actually persisted — not on a
 * no-op re-pick of the current override, and not when the mutation fails.
 * `onError` fires when persistence fails or returns no row, after the local
 * language has been reverted (the hook itself only logs — surfacing a toast
 * is left to the caller, since extension call sites have no toast host).
 * `onSkip` fires synchronously when the pick is a no-op (already the active
 * override), so callers can tear down any pending state they set up-front.
 * Exactly one of `onSuccess`/`onError`/`onSkip` fires per call.
 */
export const useUpdateUiLanguage = (
  tenantId: string,
  userId: string,
  { onSuccess, onError, onSkip }: UpdateUiLanguageCallbacks = {},
) => {
  const { language: currentLanguage, setLanguage } = useUiLanguage();
  const settings = useCompleteFragment({
    fragment: tenantUserSettingFragment,
    key: { tenantId, userId },
    strict: false,
  });
  const [mutate] = useMutation(updateUiLanguageMutation);
  const logger = useNamedLogger('useUpdateUiLanguage');

  return useCallback(
    (code: UiLanguageCode) => {
      const previousLanguage = currentLanguage;
      setLanguage(code);

      if (code === settings?.uiLanguage) {
        onSkip?.();
        return;
      }

      void mutate({
        variables: { tenantId, userId, uiLanguage: code },
        optimisticResponse: {
          updateTenantUserSettingsByPk: {
            __typename: 'TenantUserSettings',
            tenantId,
            userId,
            uiLanguage: code,
            uiLanguageOrDefault: code,
          },
        },
      })
        .then((result) => {
          if (result.data?.updateTenantUserSettingsByPk) {
            onSuccess?.(code);
            return;
          }
          logger.warn('UI language update did not persist', {
            tenantId,
            userId,
            uiLanguage: code,
          });
          setLanguage(previousLanguage);
          onError?.();
        })
        .catch((error) => {
          logger.warn('Failed to persist UI language', {
            tenantId,
            userId,
            uiLanguage: code,
            error,
          });
          setLanguage(previousLanguage);
          onError?.();
        });
    },
    [
      tenantId,
      userId,
      settings?.uiLanguage,
      currentLanguage,
      setLanguage,
      mutate,
      logger,
      onSuccess,
      onError,
      onSkip,
    ],
  );
};
